'use strict';

const libFrameworkClient = require('./providers/FrameworkClient.js');

/**
 * BlueprintShareUI
 * ----------------
 * The editor-only "share this blueprint" strip and its behavior, shared by the
 * full-page blueprint view and the compact in-detail blueprint block so the two
 * look and act the same. Pure string building + fetch helpers; the owning view
 * supplies _id, _share, render(), and pict.
 *
 * The strip carries its own classes (.bp-share*) and CSS so it works whether or
 * not it sits inside a .bp-root (the compact block does not).
 *
 * Each owning view defines five one-line delegates that the strip's buttons call:
 *   bpShareCreate / bpShareCopy / bpShareRegenerate / bpShareRevoke / bpShareSetExpiry
 */

const DURATIONS = [{ v: 0, l: 'No expiry' }, { v: 7, l: '7 days' }, { v: 30, l: '30 days' }, { v: 90, l: '90 days' }];

const _ICON_SHARROW = '<span class="pict-icon"><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" x2="12" y1="2" y2="15"/></svg></span>';
const _ICON_GLOBE = '<span class="pict-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18"/></svg></span>';
const _ICON_LOCK = '<span class="pict-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg></span>';

function _escAttr(pStr)
{
	return String(pStr == null ? '' : pStr).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function shareURL(pState)
{
	if (!pState || !pState.ShareToken) { return ''; }
	let tmpOrigin = (typeof location !== 'undefined') ? location.origin : '';
	return tmpOrigin + (pState.SharePath || ('/shared/' + pState.ShareToken));
}

// The expiry preset that best matches the link's ExpiresAt (0 = no expiry).
function _selectedDays(pState)
{
	if (!pState || !pState.ExpiresAt) { return 0; }
	let tmpRemaining = Math.round((Date.parse(pState.ExpiresAt) - Date.now()) / 86400000);
	if (tmpRemaining <= 0) { return 0; }
	let tmpBest = 7;
	let tmpDelta = Infinity;
	[7, 30, 90].forEach((pD) => { let tmpDiff = Math.abs(pD - tmpRemaining); if (tmpDiff < tmpDelta) { tmpDelta = tmpDiff; tmpBest = pD; } });
	return tmpBest;
}

function _expirySelectHTML(pState, pSelfRef)
{
	let tmpSelected = _selectedDays(pState);
	let tmpOptions = DURATIONS.map((pD) => '<option value="' + pD.v + '"' + (pD.v === tmpSelected ? ' selected' : '') + '>' + pD.l + '</option>').join('');
	return '<span class="bp-share-exp-wrap">Expires <select class="bp-share-exp" onchange="' + pSelfRef + '.bpShareSetExpiry(this.value)">' + tmpOptions + '</select></span>';
}

/**
 * The "let readers print it" toggle (F-071, WI #384).
 *
 * A SECOND grant on the same link, not a display option. Reading the page and generating the printed
 * sheet set are different things: the set is compiled, which walks outward from the entity and follows
 * provenance back as far as the chain goes, so it can carry marks and titles the page itself never
 * shows. It is off by default and stays off through every other change to the link.
 *
 * It is absent, rather than disabled, on a blueprint that can never print from a public token -- a
 * curated one, whose pins are other people's artifacts. A switch that is present and does nothing
 * teaches people to distrust the switches that do.
 */
function _printToggleHTML(pState, pSelfRef)
{
	if (!pState || !pState.PrintAvailable) { return ''; }
	return '<label class="bp-share-print" title="Let anyone with this link generate the printed sheet set">'
		+ '<input type="checkbox"' + (pState.PrintEnabled ? ' checked' : '')
		+ ' onchange="' + pSelfRef + '.bpSharePrintEnabled(this.checked)"> Printable</label>';
}


/**
 * What version this link serves, and the way to move it.
 *
 * A READER OF THIS STRIP MUST BE ABLE TO SEE WHICH IT IS. A link that follows the live sheet and a
 * link frozen at Rev B behave completely differently for the person on the other end, and the
 * difference is invisible unless the strip says so. "Pinned to Rev B" is the normal case, because
 * creating a link freezes the sheet; a link that says "follows the live sheet" is either older than
 * versioning or was deliberately unpinned.
 */
function _versionHTML(pState, pSelfRef)
{
	if (!pState || !pState.Enabled) { return ''; }
	let tmpRev = String(pState.RevisionMark || '');
	if (Number(pState.IDBlueprintRevision) > 0)
	{
		return '<span class="bp-share-ver" title="Whoever opens this link sees the sheet as it was when the link was made, however far it has moved since">'
			+ 'Pinned to ' + (tmpRev ? ('Rev ' + _escAttr(tmpRev)) : 'a version')
			+ '<button class="bp-share-btn bp-share-btn-ghost" onclick="' + pSelfRef + '.bpShareRepin()"'
			+ ' title="Take a new version of the sheet as it is now and point this same link at it">'
			+ 'Send the current version</button></span>';
	}
	return '<span class="bp-share-ver bp-share-ver-live" title="This link shows whatever the sheet says today, so it changes as the work changes">'
		+ 'Follows the live sheet'
		+ '<button class="bp-share-btn bp-share-btn-ghost" onclick="' + pSelfRef + '.bpShareRepin()"'
		+ ' title="Freeze the sheet as it is now so this link stops changing">Freeze it</button></span>';
}

// The strip HTML. pSelfRef is the JS expression for the owning view, e.g.
// "_Pict.views['Plansheet-Blueprint']".
function stripHTML(pState, pSelfRef)
{
	if (pState && pState.Enabled && pState.ShareToken)
	{
		return '<div class="bp-share bp-share-on">'
			+ '<span class="bp-share-ic">' + _ICON_GLOBE + '</span>'
			+ '<span class="bp-share-lab">Public link on</span>'
			+ '<input class="bp-share-url" readonly value="' + _escAttr(shareURL(pState)) + '" onclick="this.select()">'
			+ '<button class="bp-share-btn" onclick="' + pSelfRef + '.bpShareCopy()">Copy</button>'
			+ '<button class="bp-share-btn" onclick="' + pSelfRef + '.bpShareCopyBadge()" title="Copy a Markdown blueprint badge for a README (npm / GitHub)">README badge</button>'
			+ _expirySelectHTML(pState, pSelfRef)
			+ '<button class="bp-share-btn bp-share-btn-ghost" onclick="' + pSelfRef + '.bpShareRegenerate()" title="Replace with a new link and disable the old one">' + _ICON_SHARROW + 'Regenerate</button>'
			+ '<button class="bp-share-btn bp-share-btn-ghost" onclick="' + pSelfRef + '.bpShareRevoke()">Turn off</button>'
			+ _printToggleHTML(pState, pSelfRef)
			+ _versionHTML(pState, pSelfRef)
			+ '<div class="bp-share-invite"><input class="bp-share-invite-to" placeholder="Email the link to someone..." onkeydown="if(event.key===\'Enter\'){' + pSelfRef + '.bpShareInvite();}"><button class="bp-share-btn" onclick="' + pSelfRef + '.bpShareInvite()">' + _ICON_SHARROW + 'Email it</button></div>'
		+ '</div>';
	}
	return '<div class="bp-share">'
		+ '<span class="bp-share-ic">' + _ICON_LOCK + '</span>'
		+ '<span class="bp-share-lab">This blueprint is private. Create a public link anyone can open without signing in.</span>'
		+ '<button class="bp-share-btn" onclick="' + pSelfRef + '.bpShareCreate()">' + _ICON_SHARROW + 'Create public link</button>'
	+ '</div>';
}

// ---- behavior (operates on the owning view) ----------------------------------
function _toast(pView, pMessage, pType)
{
	let tmpModal = pView.pict && pView.pict.views && pView.pict.views['Pict-Section-Modal'];
	if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: pType || 'success' }); }
}

// Repaint after a state change via the view's own hook, so the full-page view can
// re-render cheaply while the compact block redraws only itself (no entity re-fetch).
function _repaint(pView)
{
	if (typeof pView._afterShareChange === 'function') { pView._afterShareChange(); }
	else { pView.render(); }
}

function _apply(pView, pState, pMessage)
{
	pView._share = pState;
	_repaint(pView);
	_toast(pView, pMessage);
}

// Which blueprint a strip is managing, as a BlueprintAPI subject.
//
// A strip used to sniff an entity key off its host view's private fields (_entityKey, else _key, else the
// literal 'WorkItem'). That was wrong in a way that did not look wrong: a blueprint addressed as itself
// produced /1.0/Blueprint/<id>/Blueprint/Share, which matches the generic wildcard share route, resolves
// the type through the Connections manifest, finds no 'Blueprint' key there, and answers a cheerful
// 200 { Exists: false } -- so the strip said "this blueprint is private" about a blueprint that might
// already be shared. A subject cannot express that mistake; the provider owns the address.
function _subject(pView, pID)
{
	let tmpAPI = pView && pView.pict && pView.pict.providers && pView.pict.providers.BlueprintAPI;
	if (!tmpAPI) { return null; }
	if (pView._subject && pView._subject.ID) { return pView._subject; }
	let tmpKey = (pView._entityKey || pView._key || '');
	if (tmpKey === 'Blueprint') { return tmpAPI.subjectForRecord(pID || pView._id); }
	return tmpAPI.subjectForEntity(tmpKey || 'WorkItem', pID || pView._id);
}

function _api(pPict) { return pPict && pPict.providers && pPict.providers.BlueprintAPI; }

// Share management runs in the authenticated app (the owner's share strip), so
// every call rides the shared RestClient and inherits session recovery.
function fetchState(pPict, pSubject)
{
	let tmpAPI = _api(pPict);
	return tmpAPI ? tmpAPI.shareState(pSubject) : Promise.resolve(null);
}

function _post(pPict, pSubject, pBody)
{
	let tmpAPI = _api(pPict);
	return tmpAPI ? tmpAPI.shareCreate(pSubject, pBody || {}).catch(() => null) : Promise.resolve(null);
}

function create(pView)
{
	_post(pView.pict, _subject(pView), {}).then((pD) => { if (pD && pD.ShareToken) { _apply(pView, pD, 'Public link created'); } else { _toast(pView, 'Could not create the link.', 'error'); } });
}

function setExpiry(pView, pDays)
{
	let tmpDays = parseInt(pDays, 10) || 0;
	_post(pView.pict, _subject(pView), { ExpiresInDays: tmpDays }).then((pD) => { if (pD && pD.ShareToken) { _apply(pView, pD, tmpDays > 0 ? 'Expiry set' : 'Expiry removed'); } else { _toast(pView, 'Could not update the expiry.', 'error'); } });
}

function setPrintEnabled(pView, pEnabled)
{
	let tmpEnabled = !!pEnabled;
	_post(pView.pict, _subject(pView), { PrintEnabled: tmpEnabled }).then((pD) =>
	{
		if (pD && pD.ShareToken) { _apply(pView, pD, tmpEnabled ? 'Readers can print this link' : 'Printing turned off for this link'); }
		else { _toast(pView, 'Could not change printing for the link.', 'error'); }
	});
}

function regenerate(pView)
{
	_post(pView.pict, _subject(pView), { Rotate: true }).then((pD) => { if (pD && pD.ShareToken) { _apply(pView, pD, 'New public link generated'); } else { _toast(pView, 'Could not regenerate the link.', 'error'); } });
}

function revoke(pView)
{
	_api(pView.pict).shareRevoke(_subject(pView)).catch(() => null)
		.then((pD) => { if (pD) { if (pView._share) { pView._share.Enabled = false; } _repaint(pView); _toast(pView, 'Public link turned off'); } else { _toast(pView, 'Could not turn off the link.', 'error'); } });
}

function copy(pView)
{
	let tmpURL = shareURL(pView._share);
	if (!tmpURL) { return; }
	if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(tmpURL); }
	_toast(pView, 'Public link copied');
}

// The Markdown for a README blueprint badge: the chip image (which mirrors the blueprint's style) linked
// to the public page. Paste into an npm or GitHub README; it needs no auth and caches as any badge does.
function copyBadge(pView)
{
	let tmpURL = shareURL(pView._share);
	if (!tmpURL) { return; }
	let tmpName = (pView._data && (pView._data.Title || pView._data.Name)) || 'Blueprint';
	let tmpMarkdown = '[![' + tmpName + ' blueprint](' + tmpURL + '/chip.svg)](' + tmpURL + ')';
	if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText)
	{
		navigator.clipboard.writeText(tmpMarkdown).then(() => _toast(pView, 'README badge copied -- paste it into a README')).catch(() => _toast(pView, tmpMarkdown, 'info'));
	}
	else { _toast(pView, tmpMarkdown, 'info'); }
}

// Email the public link to someone. Ensures a share exists server-side and sends
// the blueprint-invite template; the message goes to the dev log unless a real
// email provider is configured.
function invite(pView)
{
	let tmpEl = (typeof document !== 'undefined') ? document.getElementById('bp-share-invite-to') : null;
	let tmpEmail = tmpEl ? String(tmpEl.value || '').trim() : '';
	if (!tmpEmail || tmpEmail.indexOf('@') < 1) { _toast(pView, 'Enter an email address.', 'error'); return; }
	_api(pView.pict).shareInvite(_subject(pView), { Email: tmpEmail })
		.then((pD) =>
		{
			if (pD && pD.Success) { if (tmpEl) { tmpEl.value = ''; } _toast(pView, 'Invite sent to ' + tmpEmail); }
			else { _toast(pView, 'Could not send the invite.', 'error'); }
		}).catch(() => _toast(pView, 'Could not send the invite.', 'error'));
}

const SHARE_CSS = /*css*/`
	.bp-share { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 0 0 12px; padding: 10px 14px; border: 1px solid rgba(150,180,220,0.3); border-radius: 9px; background: rgba(8,16,30,0.42); color: #cfe0f6; font-size: 13px; }
	.bp-share .pict-icon { display: inline-flex; align-items: center; justify-content: center; }
	.bp-share-ic { color: #88a8d6; display: inline-flex; align-items: center; flex: 0 0 auto; }
	.bp-share-on .bp-share-ic { color: #f0c44a; }
	.bp-share-lab { flex: 1 1 200px; min-width: 120px; line-height: 1.4; }
	.bp-share-on .bp-share-lab { flex: 0 0 auto; font-family: ui-monospace, Menlo, monospace; letter-spacing: 1px; text-transform: uppercase; font-size: 11px; color: #f0c44a; }
	.bp-share-url { flex: 1 1 220px; min-width: 120px; height: 30px; box-sizing: border-box; background: #0c1830; border: 1px solid rgba(208,226,247,0.4); border-radius: 6px; color: #eef4fc; padding: 0 9px; font-size: 12px; font-family: ui-monospace, Menlo, monospace; }
	.bp-share-exp-wrap { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: #9fb6d6; }
	.bp-share-print { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: #9fb6d6; cursor: pointer; white-space: nowrap; }
	.bp-share-print input { accent-color: #f0c44a; cursor: pointer; }
	.bp-share-exp { height: 30px; box-sizing: border-box; background: #0c1830; border: 1px solid rgba(208,226,247,0.4); border-radius: 6px; color: #eef4fc; padding: 0 6px; font-size: 12px; }
	.bp-share-btn { display: inline-flex; align-items: center; gap: 6px; height: 30px; box-sizing: border-box; padding: 0 12px; border-radius: 7px; border: 1.5px solid #f0c44a; background: transparent; color: #f0c44a; cursor: pointer; font: inherit; font-size: 12.5px; white-space: nowrap; text-decoration: none; }
	.bp-share-btn:hover { background: rgba(240,196,74,0.14); }
	.bp-share-btn .pict-icon { font-size: 15px; }
	.bp-share-btn-ghost { border-color: rgba(150,180,220,0.4); color: #cfe0f6; }
	.bp-share-ver { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; color: #f0c44a; white-space: nowrap; }
	.bp-share-ver-live { color: #9fb6d6; }
	.bp-versions { margin: 0 0 12px; padding: 10px 14px; border: 1px solid rgba(150,180,220,0.3); border-radius: 9px; background: rgba(8,16,30,0.42); color: #cfe0f6; font-size: 13px; }
	.bp-versions-head { font-family: ui-monospace, Menlo, monospace; letter-spacing: 1px; text-transform: uppercase; font-size: 11px; color: #f0c44a; margin: 0 0 7px; }
	.bp-versions-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 5px; }
	.bp-ver { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 4px 0; border-top: 1px solid rgba(150,180,220,0.14); }
	.bp-ver:first-child { border-top: 0; }
	.bp-ver-mark { font-family: ui-monospace, Menlo, monospace; color: #f0c44a; flex: 0 0 auto; }
	.bp-ver-when { color: #9fb6d6; font-size: 12px; }
	.bp-ver-to, .bp-ver-n { color: #7f97b8; font-size: 12px; }
	.bp-ver-open { margin-left: auto; color: #cfe0f6; text-decoration: none; border: 1.5px solid rgba(150,180,220,0.4); border-radius: 7px; padding: 2px 11px; font-size: 12.5px; }
	.bp-ver-open:hover { background: rgba(240,196,74,0.14); border-color: #f0c44a; color: #f0c44a; }
	.bp-share-btn-ghost:hover { border-color: rgba(208,226,247,0.6); background: rgba(150,180,220,0.12); }
	.bp-share-invite { flex: 1 1 100%; display: flex; gap: 6px; margin-top: 4px; }
	.bp-share-invite-to { flex: 1 1 200px; min-width: 140px; height: 30px; box-sizing: border-box; background: #0c1830; border: 1px solid rgba(208,226,247,0.4); border-radius: 6px; color: #eef4fc; padding: 0 9px; font-size: 12px; }
`;


/**
 * Take a version of the sheet as it is now, and point this same link at it.
 *
 * SAME LINK, NEW CONTENTS, ON PURPOSE. The alternative -- mint a fresh link for every update -- means
 * a customer collects a pile of URLs and has to be told which is current. Here the URL they already
 * have starts showing the newer version the moment somebody decides it should.
 */
function repin(pView)
{
	let tmpAPI = _api(pView.pict);
	if (!tmpAPI) { return Promise.resolve(null); }
	_toast(pView, 'Taking a version...');
	// THE BLUEPRINT ROW, not the subject. A Feature and its blueprint are different ids, and versioning
	// the subject would file the snapshot where nothing looks for it. The share state carries the row id
	// precisely so callers do not have to guess.
	let tmpIDBlueprint = Number(pView._share && pView._share.IDBlueprint) || 0;
	if (!tmpIDBlueprint) { return Promise.resolve(_toast(pView, 'No sheet to version yet.', 'error')); }
	return tmpAPI.takeVersion(tmpIDBlueprint)
		.then((pTaken) =>
		{
			let tmpID = Number(pTaken && pTaken.Version && pTaken.Version.IDBlueprintRevision) || 0;
			if (!tmpID) { throw new Error('no version came back'); }
			return _post(pView.pict, _subject(pView), { IDBlueprintRevision: tmpID })
				.then((pState) => _apply(pView, pState, 'This link now serves Rev ' + (pTaken.Revision || '')));
		})
		.catch(() => _toast(pView, 'Could not take a version of this sheet.', 'error'));
}

module.exports = { repin, setPrintEnabled, stripHTML, shareURL, fetchState, create, setExpiry, regenerate, revoke, copy, copyBadge, invite, SHARE_CSS, DURATIONS };
